/**
 * Recovery metadata is additive to the GraphQL error contract. Keep this
 * reader deliberately tolerant: an older backend may omit every field, and a
 * newer backend may add unrelated extension fields.
 *
 * One reader for App and Book (moved here unchanged from the two identical
 * apps/{app,book}/src/lib/accessRecovery.ts). The session-level reading of the
 * same metadata, which also branches on `extensions.reason`, is
 * `sessionRefusal.ts` beside this file.
 */
/** At most this many entries are read from each source. */
const MAX_ENTRIES_PER_SOURCE = 50;

export type AccessRecovery =
  | "REAUTHENTICATE"
  | "RETRY_PRESERVING_SESSION"
  | "LEAVE_SURFACE";

export interface AccessRecoveryMetadata {
  code: string | null;
  accessRecovery: AccessRecovery;
  retryable: boolean | null;
}

interface ErrorEntry {
  extensions?: unknown;
}

function metadataFromExtensions(extensions: unknown): AccessRecoveryMetadata | null {
  if (!extensions || typeof extensions !== "object") return null;
  const value = extensions as Record<string, unknown>;
  const recovery = value.accessRecovery;
  if (
    recovery !== "REAUTHENTICATE" &&
    recovery !== "RETRY_PRESERVING_SESSION" &&
    recovery !== "LEAVE_SURFACE"
  ) {
    return null;
  }

  return {
    code: typeof value.code === "string" ? value.code : null,
    accessRecovery: recovery,
    retryable: typeof value.retryable === "boolean" ? value.retryable : null,
  };
}

/**
 * Extract the first recognised recovery instruction from Apollo 3/4 and raw
 * non-2xx response shapes. Unknown or absent metadata intentionally returns
 * null so callers retain their existing compatibility behaviour.
 */
export function accessRecoveryFrom(error: unknown): AccessRecoveryMetadata | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as {
    errors?: readonly ErrorEntry[];
    graphQLErrors?: readonly ErrorEntry[];
    bodyText?: unknown;
    cause?: ErrorEntry;
  };
  const entries: ErrorEntry[] = [];

  // Loops capped per source, never a spread (a huge array cannot overflow the stack).
  const append = (list: unknown) => {
    if (!Array.isArray(list)) return;
    const limit = Math.min(list.length, MAX_ENTRIES_PER_SOURCE);
    for (let i = 0; i < limit; i += 1) entries.push(list[i] as ErrorEntry);
  };
  append(candidate.errors);
  append(candidate.graphQLErrors);
  if (candidate.cause) entries.push(candidate.cause);
  if (typeof candidate.bodyText === "string") {
    try {
      const parsed = JSON.parse(candidate.bodyText) as { errors?: unknown };
      append(parsed.errors);
    } catch {
      // A gateway body is not a recovery contract.
    }
  }

  for (const entry of entries) {
    const metadata = metadataFromExtensions(entry?.extensions);
    if (metadata) return metadata;
  }
  return null;
}
